import { useState, useEffect, useRef } from 'react';
import { toAbsoluteUrl } from '../../../_metronic/helpers';

interface OptimizedImageProps {
    src: string;
    alt: string;
    className?: string;
    onClick?: () => void;
    loading?: 'lazy' | 'eager';
    aspectRatio?: string;
    fallbackSrc?: string;
    priority?: boolean;
    onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}


export const OptimizedImage: React.FC<OptimizedImageProps> = ({
    src,
    alt,
    className = '',
    onClick,
    loading = 'lazy',
    aspectRatio,
    fallbackSrc = toAbsoluteUrl('/media/logos/mainLogo.png'),
    priority = false,
    onError
}) => {
    const [imageSrc, setImageSrc] = useState<string>('');
    const [imageLoading, setImageLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    // Generate tiny blur placeholder (10x10 px data URI)
    const blurPlaceholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10"%3E%3Cfilter id="b"%3E%3CfeGaussianBlur stdDeviation="1"/%3E%3C/filter%3E%3Crect width="10" height="10" fill="%23e5e7eb" filter="url(%23b)"/%3E%3C/svg%3E';

    useEffect(() => {
        if (!src) {
            setImageSrc(fallbackSrc);
            setImageLoading(false);
            return;
        }

        // Priority images load immediately
        if (priority) {
            loadImage(src);
            return;
        }

        // Lazy load with Intersection Observer
        if (imgRef.current && loading === 'lazy') {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            loadImage(src);
                            if (observerRef.current && imgRef.current) {
                                observerRef.current.unobserve(imgRef.current);
                            }
                        }
                    });
                },
                {
                    rootMargin: '50px', // Start loading 50px before visible
                    threshold: 0.01
                }
            );

            observerRef.current.observe(imgRef.current);
        } else {
            loadImage(src);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [src]);

    const loadImage = (imageSrc: string) => {
        const img = new Image();

        // Request smaller image size from server if possible
        // You can modify this based on your server's image resize capabilities
        const optimizedSrc = imageSrc;

        img.onload = () => {
            setImageSrc(optimizedSrc);
            setImageLoading(false);
            setHasError(false);
        };

        img.onerror = () => {
            setImageSrc(fallbackSrc);
            setImageLoading(false);
            setHasError(true);
        };

        img.src = optimizedSrc;
    };

    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        if (!hasError) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
        onError?.(e);
    };

    return (
        <div
            className={`optimized-image-wrapper ${className}`}
            style={{
                aspectRatio: aspectRatio || 'auto',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Blur placeholder */}
            {imageLoading && (
                <img
                    src={blurPlaceholder}
                    alt=""
                    className="optimized-image-placeholder"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'blur(10px)',
                        transform: 'scale(1.1)'
                    }}
                />
            )}

            {/* Actual image */}
            <img
                ref={imgRef}
                src={imageSrc || blurPlaceholder}
                alt={alt}
                className={`optimized-image ${imageLoading ? 'loading' : 'loaded'}`}
                onClick={onClick}
                onError={handleError}
                loading={loading}
                decoding="async"
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: imageLoading ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out',
                    // Image rendering optimizations
                    imageRendering: '-webkit-optimize-contrast',
                    willChange: imageLoading ? 'opacity' : 'auto'
                }}
            />

            {/* Loading spinner (optional) */}
            {imageLoading && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1
                    }}
                >
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
        </div>
    );
};